// The Condition tab's Report section (#513): the letter's addressee, the site
// summary, the recommendation and the substrate notes typed at the desk (saved
// on the visit like any other field, so they sync), then External / Internal /
// Both and "Make the condition report". The report is made on the server from
// what CRM holds, saved to the job's "Condition report" folder on SharePoint as
// the next version, and opens from there in a new tab — never a copy on the
// device (DD, 2026-10-19). Making it needs signal.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { Button, Label } from '@higgins/ui';
import { ApiError, isConnectionFailure, listConditionReportsApi, makeConditionReportApi, type IssuedReportFromApi, type ReportSides } from '../lib/api';
import { patchFields, type DeviceRecord, type Fields } from '../lib/device-db';
import { isoToday } from '../lib/dates';
import { openWaitingTab } from '../lib/file-handoff';
import { useDeviceQuery, useOnline } from '../lib/hooks';
import { loadSurfaceTypes } from '../lib/reference';
import { parseSitePlan } from '../lib/site-plan';
import { markedUpStatus } from '../lib/site-plan-pins';
import { Notice } from './message';
import { SavedDocumentResult, type SavedResult } from './saved-document-result';
import { IssuedReportList } from './issued-report-list';
import { ReportContact } from './report-contact';
import { VisitSignatures } from './signature-pad';
import { visitLocked } from '../lib/visit-steps';
import { SavedTextField, TEXT_MAX } from './saved-text-field';

const SIDES: readonly ReportSides[] = ['External', 'Internal', 'Both'];
const JSON_VERSION = 1;
const NOTE_MAX = 4000;
const NOTE_ROWS = 3;

const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/** hig_reportdetails' edited substrate notes (api condition-json-check.ts). */
function substrateNotes(raw: unknown): Record<string, string> {
  try {
    const doc = JSON.parse(text(raw) || '{}') as { substrates?: Record<string, string> };
    return doc.substrates ?? {};
  } catch {
    return {};
  }
}

/** One substrate's note changed: merged into the visit's stored notes, a blank one taken out (the report then writes it). */
const saveSubstrateNote = (visitId: string, substrate: string, note: string) =>
  patchFields(visitId, (stored): Fields => {
    const substrates = { ...substrateNotes(stored.hig_reportdetails), [substrate]: note.trim() };
    if (!note.trim()) delete substrates[substrate];
    return { hig_reportdetails: JSON.stringify({ schemaVersion: JSON_VERSION, substrates }) };
  });

/** The rated substrates (surface types) across the visit, as the report names them. */
function ratedSubstrates(records: readonly DeviceRecord[], visitId: string, typeName: ReadonlyMap<string, string>): string[] {
  const visitAreas = new Set(records.filter((r) => r.table === 'visitarea' && r.parentId === visitId).map((r) => r.id));
  const names = records
    .filter((r) => r.table === 'observation' && visitAreas.has(r.parentId) && text(r.fields.hig_rating))
    .map((o) => typeName.get(text(o.fields.hig_surfacetypeid).toLowerCase()) || text(o.fields.hig_basesurface) || text(o.fields.hig_name));
  return [...new Set(names.filter(Boolean))];
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Making the condition report needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The condition report could not be made.';
}

export function ConditionReportPanel({ jobId, visit, records }: { jobId: string; visit: DeviceRecord; records: DeviceRecord[] }) {
  const online = useOnline();
  const types = useDeviceQuery(async () => (await loadSurfaceTypes()).list, []);
  const typeName = new Map((types ?? []).map((t) => [t.id.toLowerCase(), t.name]));
  const notes = substrateNotes(visit.fields.hig_reportdetails);
  const substrates = ratedSubstrates(records, visit.id, typeName);
  const plan = parseSitePlan(visit.fields.hig_siteplan);
  const planStatus = plan ? markedUpStatus(plan, records) : null;
  const waiting = records.some((r) => r.state === 'pending');
  const [sides, setSides] = useState<ReportSides>('Both');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SavedResult | null>(null);
  const [earlier, setEarlier] = useState<IssuedReportFromApi[] | null>(null);

  const loadEarlier = useCallback(() => {
    listConditionReportsApi(visit.id).then((r) => setEarlier(r.reports), () => setEarlier(null));
  }, [visit.id]);
  useEffect(() => {
    if (online) loadEarlier();
  }, [online, loadEarlier]);

  const save = (column: string) => (value: string) => patchFields(visit.id, { [column]: value });

  async function make() {
    setBusy(true);
    setResult(null);
    const tab = openWaitingTab('Making the condition report…');
    try {
      const made = await makeConditionReportApi(visit.id, sides, isoToday());
      if (tab) tab.show(made.webUrl);
      const where = tab ? tab.where : 'open it below';
      setResult({ ok: true, text: `${made.fileName} — made from ${made.template}, saved to the job's Condition report folder and ${where}.`, url: made.webUrl });
      loadEarlier();
    } catch (err) {
      tab?.close();
      setResult({ ok: false, text: `${describe(err)} Nothing was saved; make it again once that's sorted.` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div>
        <h3 className="font-medium text-slate-900">Condition report</h3>
        <p className="text-xs text-slate-500">
          The client&rsquo;s report in Word: their own format where their CRM account has one, else the Higgins standard. It takes every area&rsquo;s rating, ticks, notes, action and feature photo from the Condition tab, and the marked-up site plan.
        </p>
      </div>

      <ReportContact visit={visit} online={online} />
      {/* Keyed: a pad keeps its saved drawing (#616) and the name its typing, so neither may carry to another visit. */}
      <VisitSignatures key={visit.id} visit={visit} locked={visitLocked(visit)} />
      <div>
        <Label htmlFor="report-addressee">Addressee</Label>
        <SavedTextField id="report-addressee" label="Addressee" value={text(visit.fields.hig_addressee)} onSave={save('hig_addressee')} placeholder="Left blank, the contact above" maxLength={TEXT_MAX} className="mt-1" />
      </div>
      <div>
        <Label htmlFor="report-summary">Site summary</Label>
        <SavedTextField id="report-summary" label="Site summary" rows={NOTE_ROWS} value={text(visit.fields.hig_siteconditionsummary)} onSave={save('hig_siteconditionsummary')} maxLength={NOTE_MAX} className="mt-1" />
      </div>
      {substrates.length > 0 && (
        <div>
          <Label>Substrate notes</Label>
          <p className="text-xs text-slate-500">Left blank, the report writes each note from the ratings and defects. What you type here is kept.</p>
          {substrates.map((s) => (
            <div key={s} className="mt-2">
              <Label htmlFor={`report-substrate-${s}`} className="text-xs text-slate-600">{s}</Label>
              <SavedTextField id={`report-substrate-${s}`} label={`${s} note`} rows={2} value={notes[s] ?? ''} onSave={(note) => saveSubstrateNote(visit.id, s, note)} maxLength={NOTE_MAX} />
            </div>
          ))}
        </div>
      )}
      <div>
        <Label htmlFor="report-recommendation">Recommendation</Label>
        <SavedTextField id="report-recommendation" label="Recommendation" rows={NOTE_ROWS} value={text(visit.fields.hig_recommendation)} onSave={save('hig_recommendation')} placeholder="Left blank, written from the areas' ratings, worst first" maxLength={NOTE_MAX} className="mt-1" />
      </div>
      <div>
        <Label htmlFor="report-key-risks">Key risks</Label>
        <SavedTextField id="report-key-risks" label="Key risks" rows={NOTE_ROWS} value={text(visit.fields.hig_keyrisks)} onSave={save('hig_keyrisks')} placeholder="Left blank, the defects tapped most often, numbered" maxLength={NOTE_MAX} className="mt-1" />
      </div>

      {!plan && <p className="text-xs text-slate-500">No site plan: the report leaves Included elevations out.</p>}
      {plan && planStatus !== 'current' && (
        <Notice>
          <p>{planStatus === 'none' ? 'The site plan has no marked-up copy yet, so the report would leave the plan out.' : 'The marked-up site plan is out of date: pins have moved or been added since it was saved.'}</p>
          <Link to={`/job/${jobId}/site-plan`} className="mt-2 inline-flex min-h-11 items-center text-sm text-primary underline">
            Update marked-up copy
          </Link>
        </Notice>
      )}

      <div>
        <Label>Areas in the report</Label>
        <div className="mt-1 flex gap-2">
          {SIDES.map((s) => (
            <button key={s} type="button" aria-pressed={sides === s} onClick={() => setSides(s)} className={`min-h-11 rounded-md border px-3 text-sm ${sides === s ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Button onClick={() => void make()} disabled={busy || !online}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileText className="mr-1 h-4 w-4" />} {busy ? 'Making the condition report…' : 'Make the condition report'}
        </Button>
        {!online && <p className="mt-1 text-xs text-slate-500">Making the report needs signal.</p>}
        {online && waiting && <p className="mt-1 text-xs text-slate-500">Some changes are still waiting to sync; the report shows what CRM has.</p>}
        <SavedDocumentResult result={result} />
      </div>

      {earlier && earlier.length > 0 && <IssuedReportList reports={earlier} onChanged={loadEarlier} />}
    </div>
  );
}
